import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { resolveAuthorFunction, resolveDenomination, resolveDomain, resolveInstitution, resolveLastReportDate } from '@skinsoft/entity-ui/src/item/serializer/resolve-fields';
import { SearchFeatured } from '@skinsoft/ui';

import { isWemi, SearchResult, thesaurusSelectors, Wemi } from '@skinsoft/entity';
import { searchResultSelectors, wemiSelectors } from '..';


export function toSearchFeatured$(itemSearchResult: SearchResult | Wemi, locale: string, fromSearch: boolean = true): Observable<SearchFeatured> {

    let searchFeatured: SearchFeatured = null as any;
    if (isWemi(itemSearchResult)) {
        searchFeatured = {
            creation_date: wemiSelectors.creationDate(itemSearchResult as Wemi),
            credits: wemiSelectors.credits(itemSearchResult as Wemi), // author name and function
            denomination: wemiSelectors.denomination(itemSearchResult as Wemi),  // ex  : sculpture, painting etc ...
            domain: wemiSelectors.domain(itemSearchResult as Wemi),  // ex  : sculpture, painting etc ...
            expressionId: wemiSelectors.expressionId(itemSearchResult as Wemi),
            institution: wemiSelectors.institution(itemSearchResult as Wemi),
            inventoryNumber: wemiSelectors.inventoryNumber(itemSearchResult as Wemi, locale),
            itemId: wemiSelectors.id(itemSearchResult as Wemi),
            itemType: wemiSelectors.itemType(itemSearchResult as Wemi),
            lastConditionReportId: wemiSelectors.lastConditionReport(itemSearchResult as Wemi),
            lastDateOfConditionReport: wemiSelectors.lastDateOfConditionReport(itemSearchResult as Wemi),
            manifestationId: wemiSelectors.manifestationId(itemSearchResult as Wemi),
            materialAndTechniques: wemiSelectors.materialsAndTechnics(itemSearchResult as Wemi, locale, fromSearch),
            measurements: wemiSelectors.measurements(itemSearchResult as Wemi, locale, fromSearch),
            media: wemiSelectors.media(itemSearchResult as Wemi),
            title: wemiSelectors.title(itemSearchResult as Wemi, locale),
            weight: wemiSelectors.weight(itemSearchResult as Wemi),
            workId: wemiSelectors.workId(itemSearchResult as Wemi),
        };
    } else {
        searchFeatured = {
            creation_date: searchResultSelectors.creationDate(itemSearchResult as SearchResult),
            credits: searchResultSelectors.credits(itemSearchResult as SearchResult),
            denomination: searchResultSelectors.denomination(itemSearchResult as SearchResult),
            domain: searchResultSelectors.domain(itemSearchResult as SearchResult),
            expressionId: "",
            institution: searchResultSelectors.institution(itemSearchResult as SearchResult),
            inventoryNumber: searchResultSelectors.inventoryNumber(itemSearchResult as SearchResult, locale),
            itemId: searchResultSelectors.id(itemSearchResult as SearchResult),
            itemType: searchResultSelectors.itemType(itemSearchResult as SearchResult),
            lastConditionReportId: searchResultSelectors.lastConditionReport(itemSearchResult as SearchResult),
            lastDateOfConditionReport: searchResultSelectors.lastDateOfConditionReport(itemSearchResult as SearchResult),
            manifestationId: "",
            materialAndTechniques: searchResultSelectors.materialsAndTechnics(itemSearchResult as SearchResult, locale),
            measurements: searchResultSelectors.measurements(itemSearchResult as SearchResult, locale),
            media: searchResultSelectors.media(itemSearchResult as SearchResult),
            title: searchResultSelectors.title(itemSearchResult as SearchResult, locale),
            weight: searchResultSelectors.weight(itemSearchResult as SearchResult),
            workId: ""
        };
    }


    const featured$ = of(searchFeatured);

    return featured$
        .pipe(
            switchMap(
                async theSearchFeatured => {
                    await Promise.all([
                        resolveInstitution(theSearchFeatured, locale).then(result => theSearchFeatured.institution = result),
                        resolveAuthorFunction(theSearchFeatured, locale).then(result => theSearchFeatured.credits.authorFunction = result),
                        resolveDomain(theSearchFeatured, locale).then(result => theSearchFeatured.domain = result),
                        resolveDenomination(theSearchFeatured, locale).then(result => theSearchFeatured.denomination = result),
                        resolveLastReportDate(theSearchFeatured).then(result => theSearchFeatured.lastDateOfConditionReport = result),
                    ])

                    return theSearchFeatured;
                }
            ),
            // Resolve measurements
            switchMap(
                theSearchFeatured => {
                    return from(theSearchFeatured.measurements).pipe(
                        map(measurements => {
                            return {
                                ...theSearchFeatured,
                                measurements: measurements || []
                            }
                        })
                    )
                }
            ),
            // Resolve materials and techniques
            switchMap(
                theSearchFeatured => {
                    return from(theSearchFeatured.materialAndTechniques).pipe(
                        map(mAndT => {
                            return {
                                ...theSearchFeatured,
                                materialAndTechniques: mAndT || []
                            }
                        })
                    )
                }
            ),
            catchError(error => featured$))
}
